/**
 * ErrorFallback — rendered inside the MRT body when the backing
 * fetch thunk fails. Surfaces the status message or validation list
 * that the API returned and offers two retry paths: keep current
 * filters (Retry) or wipe them (Retry and clear filters) — the
 * second only appears when the user actually has filters applied,
 * so on a clean table we don't show a dead button.
 *
 * Logic is lifted from the legacy version verbatim (same retry
 * signatures `onRetry(true) / onRetry(false)`, same validation-
 * error layout, same localisation keys). Styling moved onto shared
 * tokens so the card sits on the same surface hierarchy as the rest
 * of the v3 table chrome.
 */
import { Button } from 'mui/core';
import { styled } from '@mui/material/styles';
import {
  ErrorOutline as ErrorOutlineIcon,
  Refresh as RefreshIcon,
  ClearAll as ClearAllIcon,
} from 'mui/icons';
import { useSelector } from 'react-redux';

const PREFIX = 'ErrorFallback';
const classes = {
  content: `${PREFIX}-content`,
  header: `${PREFIX}-header`,
  iconContainer: `${PREFIX}-iconContainer`,
  icon: `${PREFIX}-icon`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  validationList: `${PREFIX}-validationList`,
  actions: `${PREFIX}-actions`,
  retryBtn: `${PREFIX}-retryBtn`,
  resetBtn: `${PREFIX}-resetBtn`,
};

const Container = styled('div')({
  position: 'sticky',
  left: '50%',
  right: '50%',
  maxWidth: 1,

  [`& .${classes.content}`]: {
    position: 'absolute',
    width: 560,
    maxWidth: 720,
    transform: 'translate(-50%, 40px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '40px 64px',
    borderRadius: 12,
    border: '1px dashed var(--qhs-border)',
    background: 'var(--qhs-surface)',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  [`& .${classes.iconContainer}`]: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'var(--qhs-st-critical-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  [`& .${classes.icon}`]: {
    fontSize: 20,
    color: 'var(--qhs-st-critical-tx)',
  },
  [`& .${classes.title}`]: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--qhs-text-1)',
    fontFamily: 'var(--qhs-font-body)',
  },
  [`& .${classes.subtitle}`]: {
    fontSize: 12,
    color: 'var(--qhs-text-3)',
    fontFamily: 'var(--qhs-font-body)',
    textAlign: 'center',
    lineHeight: 1.5,
  },
  [`& .${classes.validationList}`]: {
    margin: 0,
    paddingLeft: 16,
    fontSize: 12,
    color: 'var(--qhs-st-critical-tx)',
    fontFamily: 'var(--qhs-font-mono)',
    lineHeight: 1.6,
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 4,
    width: '100%',
    maxWidth: 260,
  },
  [`& .${classes.retryBtn}`]: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 14px',
    minHeight: 32,
  },
  [`& .${classes.resetBtn}`]: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 500,
    padding: '6px 14px',
    minHeight: 32,
    color: 'var(--qhs-text-2)',
    borderColor: 'var(--qhs-border)',
  },
});

const getErrorSubtitle = (error, tables) => {
  if (error.validationErrors) return null;
  if (error.status >= 500) {
    return tables?.unexpectedServerError || 'An unexpected server error occurred';
  }
  return error.message || tables?.failedToLoadData || 'Failed to load data';
};

const ErrorFallback = ({ error, onRetry, hasActiveFilters }) => {
  const { tables, btn } = useSelector(state => state.i18n);
  const subtitle = getErrorSubtitle(error, tables);
  const validationErrors = error?.validationErrors;

  const handleRetry = () => onRetry?.(true);
  const handleRetryWithReset = () => onRetry?.(false);

  return (
    <Container className="table-fallback">
      <div className={classes.content}>
        <div className={classes.header}>
          <div className={classes.iconContainer}>
            <ErrorOutlineIcon className={classes.icon} />
          </div>
          <div className={classes.title}>{tables?.unableToLoadData || 'Unable to load data'}</div>
        </div>

        {subtitle && <div className={classes.subtitle}>{subtitle}</div>}

        {validationErrors && (
          <ul className={classes.validationList}>
            {Object.entries(validationErrors).map(([field, message]) => (
              <li key={field}>{message}</li>
            ))}
          </ul>
        )}

        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.retryBtn}
            startIcon={<RefreshIcon style={{ fontSize: 16 }} />}
            onClick={handleRetry}
          >
            {btn?.retry || 'Retry'}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              className={classes.resetBtn}
              startIcon={<ClearAllIcon style={{ fontSize: 16 }} />}
              onClick={handleRetryWithReset}
            >
              {btn?.retryAndClearFilters || 'Retry and clear filters'}
            </Button>
          )}
        </div>
      </div>
    </Container>
  );
};

export default ErrorFallback;
